import Button from "components/form/Button";
import moment from "moment";
import React, { useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch } from "store/hooks";
import {
  pendingSubscriptionDetailsGet,
  pendingSubscriptionUpdate,
} from "store/slices/subscriptionSlice";
import { ToastState } from "types/common";
import { CancelProps } from "types/subscriptionAndPlans";

const ScheduleUpdatePlan = ({
  id,
  setShowModal,
  show,
  setMsg,
  fetchDetails,
}: {
  id: number | null;
  setShowModal: React.Dispatch<React.SetStateAction<CancelProps>>;
  show: boolean;
  setMsg: React.Dispatch<React.SetStateAction<ToastState>>;
  fetchDetails: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [isBTNLoading, setIsBTNLoading] = React.useState(false);
  const [isDataLoading, setIsDataLoading] = React.useState(true);
  const [planDetails, setPlanDetails] = React.useState<{
    current_plan: string;
    scheduled_plan: string;
    effective_date: string;
  }>({
    current_plan: "",
    scheduled_plan: "",
    effective_date: "",
  });

  const handleCancelUpdatePlan = async () => {
    setIsBTNLoading(true);
    try {
      let res: any = await dispatch(pendingSubscriptionUpdate({ id })).unwrap();

      if (res?.body?.success) {
        setShowModal({ showModal: false, id: null });
        setMsg({
          content:
            "The scheduled update has been canceled. Your subscription will continue without changes.",
          variant: "success",
        });
        fetchDetails();
      }
    } catch (error) {
      console.log("error ======", error);
    } finally {
      setIsBTNLoading(false);
    }
  };

  const handleGetScheduleDetails = async (id: number) => {
    setIsDataLoading(true);
    try {
      const response: any = await dispatch(
        pendingSubscriptionDetailsGet({ id }),
      ).unwrap();

      if (response?.body?.success) {
        setPlanDetails({
          current_plan: response?.body?.data?.current_plan || "",
          scheduled_plan: response?.body?.data?.scheduled_plan || "",
          effective_date: response?.body?.data?.scheduled_date || "",
        });
      }
    } catch (error) {
      console.log("error ======", error);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    if (show && id) {
      handleGetScheduleDetails(id);
    }
  }, [show, id]);

  return (
    <div className="pt-5 w-full lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
      {isDataLoading ? (
        <div className="flex flex-col w-full">
          <Skeleton height={25} className="w-full" />
          <Skeleton height={25} className="w-full" />
        </div>
      ) : (
        <p className="break-words whitespace-normal">
          You have a scheduled change from{" "}
          <span>{planDetails.current_plan}</span> to{" "}
          <span>{planDetails.scheduled_plan}</span> on{" "}
          <span>
            {planDetails.effective_date
              ? moment(planDetails.effective_date).format("MMMM D, YYYY")
              : ""}
          </span>
          .
        </p>
      )}
      Canceling this update will keep your current subscription unchanged.
      <br />
      <br />
      Are you sure you want to cancel this update?
      <div className="lg:pt-[30px] pt-5 w-full flex lg:justify-end justify-center item-end gap-5">
        <Button
          text="Cancel"
          variant="white"
          className="lg:w-fit w-full"
          isDisabled={isBTNLoading || isDataLoading}
          onClick={() => setShowModal({ showModal: false, id: null })}
        />
        <Button
          text="OK"
          className="lg:w-fit w-full"
          isDisabled={isDataLoading}
          onClick={() => handleCancelUpdatePlan()}
          isLoading={isBTNLoading}
        />
      </div>
    </div>
  );
};

export default ScheduleUpdatePlan;
